import { motion } from "framer-motion";
import { useState, useEffect } from "react";

export default function Footer() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Guard against environments where IntersectionObserver is not available
    if (typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry && entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    const element = document.querySelector("footer");
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const quickLinks = [
    { name: "Home", id: "hero" },
    { name: "About", id: "about" },
    { name: "Products", id: "products" },
    { name: "Services", id: "services" },
    { name: "Gallery", id: "gallery" },
    { name: "Contact", id: "contact" },
  ];

  const productCategories = [
    "Hardware",
    "Furniture",
    "Doors",
    "Handrails",
    "Others",
  ];

  const services = [
    "Custom Railing",
    "Gates & Entrance",
    "Fences & Boundaries",
    "Wall Panels & Partitions",
    "Custom Metal Furnitures",
    "Artistic Metal Accents",
  ];

  const socialLinks = [
    { icon: "ri-facebook-fill", name: "Facebook", url: "#" },
    { icon: "ri-twitter-fill", name: "Twitter", url: "#" },
    { icon: "ri-linkedin-fill", name: "LinkedIn", url: "#" },
    { icon: "ri-instagram-fill", name: "Instagram", url: "#" },
  ];

  return (
    <footer className="relative bg-gradient-to-br from-[#225a96] via-[#1a4670] to-[#0f2d47] overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-[#d02b37]/10 to-[#ff6b7a]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-gradient-to-tr from-[#225a96]/15 to-[#0f2d47]/10 rounded-full blur-2xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-[#1a4670]/15 via-transparent to-transparent rounded-full" />

        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(245,245,245,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(245,245,245,0.1) 1px, transparent 1px)
            `,
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      <div className="relative z-10">
        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Company Info */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-1"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="flex items-center w-full md:w-fit justify-center md:justify-start  cursor-pointer group"
                onClick={() => scrollToSection("hero")}
              >
                <motion.img
                  src="/assets/logo-final.png"
                  alt="Prime Edge Logo"
                  className="lg:h-32 h-40 group-hover:brightness-125 filter drop-shadow-lg transition-all duration-300"
                  whileHover={{ rotate: [0, -5, 5, 0] }}
                  transition={{ duration: 0.6 }}
                />
                
                
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-[#f5f5f5] leading-relaxed mb-8 text-lg"
                style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
              >
                Transforming workspaces with premium office furniture and
                innovative design solutions for over 15 years.
              </motion.p>

              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="space-y-4"
              >
                <motion.div
                  className="flex items-center space-x-4 text-[#f5f5f5] hover:text-white transition-colors duration-300 group cursor-pointer"
                  whileHover={{ x: 8 }}
                >
                  <motion.i
                    className="ri-phone-line text-xl text-[#d02b37] group-hover:scale-125 transition-transform duration-300"
                    whileHover={{ rotate: 15 }}
                  />
                  <span
                    style={{
                      fontFamily: "system-ui, -apple-system, sans-serif",
                    }}
                  >
                    +961 79 124 624
                  </span>
                </motion.div>

                <motion.div
                  className="flex items-center space-x-4 text-[#f5f5f5] hover:text-white transition-colors duration-300 group cursor-pointer"
                  whileHover={{ x: 8 }}
                >
                  <motion.i
                    className="ri-mail-line text-xl text-[#d02b37] group-hover:scale-125 transition-transform duration-300"
                    whileHover={{ rotate: 15 }}
                  />
                  <span
                    style={{
                      fontFamily: "system-ui, -apple-system, sans-serif",
                    }}
                  >
                    info@primeedge-lb.com
                  </span>
                </motion.div>

                <motion.div
                  className="flex items-start space-x-4 text-[#f5f5f5] hover:text-white transition-colors duration-300 group cursor-pointer"
                  whileHover={{ x: 8 }}
                >
                  <motion.i
                    className="ri-map-pin-line text-xl text-[#d02b37] mt-1 group-hover:scale-125 transition-transform duration-300"
                    whileHover={{ rotate: 15 }}
                  />
                  <span
                    style={{
                      fontFamily: "system-ui, -apple-system, sans-serif",
                    }}
                  >
                    Saeb Salam street <br /> Beirut <br /> Lebanon
                  </span>
                </motion.div>
              </motion.div>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <motion.h3
                className="text-2xl font-bold text-white mb-8"
                style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
              >
                Quick Links
              </motion.h3>
              <ul className="space-y-4">
                {quickLinks.map((link, index) => (
                  <motion.li
                    key={link.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={isVisible ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                  >
                    <motion.button
                      onClick={() => scrollToSection(link.id)}
                      className="text-[#f5f5f5] hover:text-[#d02b37] transition-colors duration-300 flex items-center space-x-3 group"
                      whileHover={{ x: 8 }}
                      style={{
                        fontFamily: "system-ui, -apple-system, sans-serif",
                      }}
                    >
                      <motion.i
                        className="ri-arrow-right-s-line text-lg group-hover:text-[#d02b37] transition-colors duration-300"
                        whileHover={{ scale: 1.3, rotate: 90 }}
                        transition={{ duration: 0.3 }}
                      />
                      <span>{link.name}</span>
                    </motion.button>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Products */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <motion.h3
                className="text-2xl font-bold text-white mb-8"
                style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
              >
                Products
              </motion.h3>
              <ul className="space-y-4">
                {productCategories.map((category, index) => (
                  <motion.li
                    key={category}
                    initial={{ opacity: 0, x: -20 }}
                    animate={isVisible ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                  >
                    <motion.button
                      onClick={() => scrollToSection("products")}
                      className="text-[#f5f5f5] hover:text-[#d02b37] transition-colors duration-300 flex items-center space-x-3 group"
                      whileHover={{ x: 8 }}
                      style={{
                        fontFamily: "system-ui, -apple-system, sans-serif",
                      }}
                    >
                      <motion.i
                        className="ri-arrow-right-s-line text-lg group-hover:text-[#d02b37] transition-colors duration-300"
                        whileHover={{ scale: 1.3, rotate: 90 }}
                        transition={{ duration: 0.3 }}
                      />
                      <span>{category}</span>
                    </motion.button>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Services */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <motion.h3
                className="text-2xl font-bold text-white mb-8"
                style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
              >
                Services
              </motion.h3>
              <ul className="space-y-4 mb-8">
                {services.map((service, index) => (
                  <motion.li
                    key={service}
                    initial={{ opacity: 0, x: -20 }}
                    animate={isVisible ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.6, delay: 1 + index * 0.1 }}
                  >
                    <motion.button
                      onClick={() => scrollToSection("services")}
                      className="text-[#f5f5f5] hover:text-[#d02b37] transition-colors duration-300 flex items-center space-x-3 group"
                      whileHover={{ x: 8 }}
                      style={{
                        fontFamily: "system-ui, -apple-system, sans-serif",
                      }}
                    >
                      <motion.i
                        className="ri-arrow-right-s-line text-lg group-hover:text-[#d02b37] transition-colors duration-300"
                        whileHover={{ scale: 1.3, rotate: 90 }}
                        transition={{ duration: 0.3 }}
                      />
                      <span>{service}</span>
                    </motion.button>
                  </motion.li>
                ))}
              </ul>

              {/* Social Links */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 1.6 }}
              >
                <h4
                  className="text-lg font-bold text-white mb-6"
                  style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
                >
                  Follow Us
                </h4>
                <div className="flex space-x-4">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={social.name}
                      href={social.url}
                      initial={{ opacity: 0, scale: 0.7 }}
                      animate={isVisible ? { opacity: 1, scale: 1 } : {}}
                      transition={{ duration: 0.6, delay: 1.8 + index * 0.1 }}
                      whileHover={{
                        scale: 1.2,
                        backgroundColor: "rgba(208, 43, 55, 0.2)",
                        boxShadow: "0 10px 20px rgba(208, 43, 55, 0.3)",
                      }}
                      whileTap={{ scale: 0.95 }}
                      className="w-12 h-12 bg-[#225a96]/20 hover:bg-[#d02b37]/20 text-[#f5f5f0] hover:text-white rounded-2xl flex items-center justify-center transition-all duration-500 backdrop-blur-sm border border-white/20 hover:border-[#d02b37]/40"
                      aria-label={social.name}
                    >
                      <motion.i
                        className={`${social.icon} text-xl`}
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.6 }}
                      />
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Newsletter Section */}
        {/* <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 1.2 }}
          className="border-t border-white/20 bg-gradient-to-r from-[#1a4670]/30 to-[#0f2d47]/30 backdrop-blur-sm"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <motion.h3
                className="text-3xl md:text-4xl font-bold text-white mb-6"
                style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
              >
                Stay Updated with Our Latest Designs
              </motion.h3>
              <motion.p
                className="text-xl text-[#f5f5f5] mb-10 max-w-2xl mx-auto"
                style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
              >
                Subscribe to our newsletter and be the first to know about new products, exclusive offers, and design inspiration.
              </motion.p>

              <motion.div
                className="max-w-md mx-auto flex space-x-4"
                whileHover={{ scale: 1.02 }}
              >
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-6 py-4 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-[#f5f5f5]/70 focus:outline-none focus:border-[#d02b37] focus:ring-2 focus:ring-[#d02b37]/50 transition-all duration-300"
                  style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                />
                <motion.button
                  whileHover={{
                    scale: 1.08,
                    boxShadow: "0 15px 30px rgba(208, 43, 55, 0.4)",
                  }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-gradient-to-r from-[#d02b37] to-[#ff6b7a] hover:from-[#ff6b7a] hover:to-[#d02b37] text-white rounded-2xl font-bold transition-all duration-500 flex items-center space-x-2 whitespace-nowrap shadow-xl"
                  style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                >
                  <span>Subscribe</span>
                  <motion.i
                    className="ri-send-plane-line"
                    whileHover={{ rotate: 45 }}
                    transition={{ duration: 0.3 }}
                  />
                </motion.button>
              </motion.div>
            </div>
          </div>
        </motion.div> */}

        {/* Bottom Footer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.8 }}
          className="border-t border-white/20 bg-gradient-to-r from-[#0f2d47]/50 to-[#1a4670]/30 backdrop-blur-sm"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <motion.p
                className="text-[#f5f5f5] text-center md:text-left"
                style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
              >
                © 2025 Prime Edge Office Solutions. All rights reserved.
              </motion.p>

              <div className="flex items-center space-x-8">
                <motion.button
                  className="text-[#f5f5f5] hover:text-[#d02b37] transition-colors duration-300"
                  whileHover={{ scale: 1.05 }}
                  style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
                >
                  Privacy Policy
                </motion.button>
                <motion.button
                  className="text-[#f5f5f5] hover:text-[#d02b37] transition-colors duration-300"
                  whileHover={{ scale: 1.05 }}
                  style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
                >
                  Terms of Service
                </motion.button>
                <motion.a
                  href="https://readdy.ai/?origin=logo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#f5f5f5] hover:text-[#d02b37] transition-colors duration-300"
                  whileHover={{ scale: 1.05 }}
                  style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
                >
                  Powered by
                </motion.a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
}
